import { supabase } from '../lib/supabase';
import { extractPositionStartEnd, hasValidPositionData } from '../utils/positionExtraction';

export interface SyncableEvent {
  id: string;
  match_id: string;
  team_id: string;
  stat_type_id: string;
  match_minute: number;
  action: 'increment' | 'decrement';
  new_value: number;
  timestamp: string;
  user_id?: string;
}

export class KeyEventsSyncService {
  // Key event stat types
  private static readonly KEY_EVENT_STAT_TYPES = [
          // Goals
          'goal_scored',
          'shot_inside_box_goal',
          'shot_outside_box_goal', 
          'penalty_scored',
          'penalty_missed',
          'penalty_shootout_scored',
          'penalty_shootout_missed',
          'own_goal',
          // Assist
          'assist',
          // Cards
          'yellow_card',
          'red_card',
          // Substitutions
          'substitution',
    // Saves
          'save_open_play',
          'save_penalty',
          'penalty_punch',
          'penalty_claim',
          'open_play_punch',
          'open_play_claim',
          // Game flow
          'kick_off',
          'end_1st_half',
          'start_2nd_half',
          'start_extra_time_1st_half',
          'end_extra_time_1st_half',
          'start_extra_time_2nd_half',
          'penalty_shootout',
          'full_time'
  ];

  // Stat types to exclude from match_timeline
  private static readonly EXCLUDED_TIMELINE_STAT_TYPES = [
    'short_pass_success',
    'short_pass_failure',
    'short_pass_backward_success',
    'short_pass_backward_failure',
    'long_pass_success',
    'long_pass_failure',
    'long_pass_backward_success',
    'long_pass_backward_failure',
    'tackle_success',
    'tackle_failure',
    'throw_in',
    'interception',
    'clearance'
  ];

  /**
   * Sync scoresheet_logs to key_match_events, match_timeline, and match_visualization
   * This creates unpublished key events and timeline/visualization data from existing scoresheet data
   */
  static async syncMatchEvents(matchId: string): Promise<{ 
    syncedCount: number; 
    totalLogs: number;
    timelineCount: number;
    visualizationCount: number;
    videoTagsCount: number;
  }> {
    try {
      // Step 1: Get all scoresheet logs for the match with position_data
      const { data: allLogs, error: allLogsError } = await supabase
        .from('scoresheet_logs')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          player_id,
          match_minute,
          action,
          new_value,
          timestamp,
          user_id,
          position_data,
          video_time_seconds,
          video_id,
          manual_tags,
          stat_types!inner(stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('timestamp', { ascending: true });

      if (allLogsError) {
        throw new Error(`Failed to fetch scoresheet logs: ${allLogsError.message}`);
      }

      if (!allLogs || allLogs.length === 0) {
        console.log('No logs found for match:', matchId);
        return { syncedCount: 0, totalLogs: 0, timelineCount: 0, visualizationCount: 0, videoTagsCount: 0 };
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Step 2: Sync to key_match_events (filtered by key event stat types)
      const keyEventLogs = allLogs.filter(log => {
        const statType = log.stat_types as any;
        return this.KEY_EVENT_STAT_TYPES.includes(statType.stat_key);
      });

      const keyEventsResult = await this.syncKeyMatchEventsInternal(matchId, keyEventLogs, user?.id);
      
      // Step 3: Sync to match_timeline (exclude certain stat types)
      const timelineLogs = allLogs.filter(log => {
        const statType = log.stat_types as any;
        return !this.EXCLUDED_TIMELINE_STAT_TYPES.includes(statType.stat_key);
      });

      const timelineResult = await this.syncMatchTimelineInternal(matchId, timelineLogs, user?.id);
      
      // Step 4: Sync to match_visualization (only logs with position_data)
      const visualizationLogs = allLogs.filter(log => hasValidPositionData(log.position_data));
      const visualizationResult = await this.syncMatchVisualizationInternal(matchId, visualizationLogs, user?.id);

      // Step 5: Sync to match_video_tags (logs with video timing, same filter as match_timeline + manual_tags)
      const { VideoTagsSyncService } = await import('./videoTagsSyncService');
      // Get logs with video timing (filtering logic is inside VideoTagsSyncService)
      const videoTagLogs = allLogs.filter(log => {
        // Must have video timing - filtering by stat type and manual_tags is done inside VideoTagsSyncService
        return log.video_time_seconds && log.video_id;
      });
      const videoTagsResult = await VideoTagsSyncService.syncVideoTagsFromScoresheetLogs(
        matchId,
        videoTagLogs,
        user?.id
      );

      return {
        syncedCount: keyEventsResult.syncedCount,
        totalLogs: allLogs.length,
        timelineCount: timelineResult.syncedCount,
        visualizationCount: visualizationResult.syncedCount,
        videoTagsCount: videoTagsResult.syncedCount
      };
    } catch (error) {
      console.error('Error syncing match events:', error);
      throw error;
    }
  }

  /**
   * Sync logs to key_match_events table
   * - Inserts new events that don't exist
   * - Updates existing events ONLY if player_id is missing in destination but exists in source
   * - Never overwrites manually set player_id values
   * 
   * @param matchId - Match ID to sync events for
   * @param logs - Optional array of logs to sync. If not provided, fetches from scoresheet_logs
   * @param userId - Optional user ID for created_by field
   */
  static async syncKeyMatchEvents(
    matchId: string, 
    logs?: any[], 
    userId?: string
  ): Promise<{ syncedCount: number }> {
    // If logs not provided, fetch from scoresheet_logs
    if (!logs || logs.length === 0) {
      const { data: allLogs, error: allLogsError } = await supabase
        .from('scoresheet_logs')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          player_id,
          match_minute,
          action,
          new_value,
          timestamp,
          user_id,
          position_data,
          stat_types!inner(stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('timestamp', { ascending: true });

      if (allLogsError) {
        throw new Error(`Failed to fetch scoresheet logs: ${allLogsError.message}`);
      }

      logs = allLogs || [];
    }

    // Filter to key event stat types
    const keyEventLogs = logs.filter(log => {
      const statType = log.stat_types as any;
      return this.KEY_EVENT_STAT_TYPES.includes(statType.stat_key);
    });

    return this.syncKeyMatchEventsInternal(matchId, keyEventLogs, userId);
  }

  /**
   * Internal method for syncing key match events (used by both public method and orchestrator)
   */
  private static async syncKeyMatchEventsInternal(
    matchId: string, 
    logs: any[], 
    userId?: string
  ): Promise<{ syncedCount: number }> {
    if (logs.length === 0) {
      return { syncedCount: 0 };
    }

    // Get log IDs we're processing to optimize the query
    const logIds = logs.map(log => log.id).filter(Boolean);
    
    if (logIds.length === 0) {
      return { syncedCount: 0 };
    }

    // Check which events already exist (with their primary_player_id to check if it's missing)
    // Check by source_log_id regardless of is_active status to prevent duplicates
    // Only check for the specific log IDs we're processing for efficiency
    const { data: existingEvents, error: existingError } = await supabase
      .from('key_match_events')
      .select('id, source_log_id, primary_player_id')
      .eq('match_id', matchId)
      .in('source_log_id', logIds);

    if (existingError) {
      throw new Error(`Failed to check existing events: ${existingError.message}`);
    }

    // Create a map: source_log_id -> { event_id, primary_player_id }
    const existingEventsMap = new Map<string, { id: string; primary_player_id: string | null }>();
    existingEvents?.forEach(event => {
      if (event.source_log_id) {
        existingEventsMap.set(event.source_log_id, {
          id: event.id,
          primary_player_id: event.primary_player_id
        });
      }
    });

    // Separate logs into new events and events that need updates
    const newEvents: any[] = [];
    const eventsToUpdate: Array<{ eventId: string; playerId: string }> = [];

    logs.forEach(log => {
      const statType = log.stat_types as any;
      
      // Determine if this is a key event worth publishing
      const isKeyEvent = this.isKeyEvent(statType.stat_key, log.action, log.new_value);
      
      if (!isKeyEvent) return;

      const existingEvent = existingEventsMap.get(log.id);
      
      if (!existingEvent) {
        // New event - add to insert list
        newEvents.push(log);
      } else {
        // Event already exists - check if we need to update player_id
        // Only update if:
        // 1. Log has player_id
        // 2. Existing event's primary_player_id is null (missing)
        if (log.player_id && !existingEvent.primary_player_id) {
          eventsToUpdate.push({
            eventId: existingEvent.id,
            playerId: log.player_id
          });
        }
      }
    });

    let syncedCount = 0;

    // Insert new events
    if (newEvents.length > 0) {
      const eventsToInsert = newEvents.map(log => {
        const statType = log.stat_types as any;
        
        // Extract position data
        const positionExtraction = extractPositionStartEnd(log.position_data);

        // Generate tags
        const tags = this.generateEventTags(statType.stat_key, log);

        // Determine if this is a game flow event (no specific team)
        const isGameFlowEvent = ['kick_off', 'end_1st_half', 'start_2nd_half', 'full_time', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout'].includes(statType.stat_key);
        
        return {
          match_id: log.match_id,
          team_id: isGameFlowEvent ? null : log.team_id,
          stat_type_id: log.stat_type_id,
          match_minute: log.match_minute,
          primary_player_id: log.player_id || null,
          secondary_player_id: null,
          tags,
          status: 'unpublished' as const,
          source_log_id: log.id,
          created_by: userId,
          is_active: log.action === 'increment', // Decrements default to inactive
          event_timestamp: log.timestamp,
          position_start: positionExtraction.position_start,
          position_end: positionExtraction.position_end
        };
      });

      const { error: insertError } = await supabase
        .from('key_match_events')
        .insert(eventsToInsert);

      if (insertError) {
        throw new Error(`Failed to insert key events: ${insertError.message}`);
      }

      syncedCount += eventsToInsert.length;
      console.log(`Inserted ${eventsToInsert.length} new key events for match ${matchId}`);
    }

    // Update existing events with missing player_id
    if (eventsToUpdate.length > 0) {
      // Update each event individually to set primary_player_id
      const updatePromises = eventsToUpdate.map(({ eventId, playerId }) =>
        supabase
          .from('key_match_events')
          .update({ primary_player_id: playerId })
          .eq('id', eventId)
      );

      const updateResults = await Promise.all(updatePromises);
      
      // Check for errors
      const errors = updateResults.filter(result => result.error);
      if (errors.length > 0) {
        console.error(`Failed to update ${errors.length} key events with player_id`);
        // Don't throw - log error but continue
      } else {
        syncedCount += eventsToUpdate.length;
        console.log(`Updated ${eventsToUpdate.length} existing key events with missing player_id for match ${matchId}`);
      }
    }

    if (syncedCount === 0) {
      console.log('No new or updatable key events for match:', matchId);
    }

    return { syncedCount };
  }

  /**
   * Sync logs to match_timeline table
   * - Inserts new events that don't exist
   * - Updates existing events ONLY if player_id is missing in destination but exists in source
   * - Never overwrites manually set player_id values
   * 
   * @param matchId - Match ID to sync events for
   * @param logs - Optional array of logs to sync. If not provided, fetches from scoresheet_logs
   * @param userId - Optional user ID for created_by field
   */
  static async syncMatchTimeline(
    matchId: string,
    logs?: any[],
    userId?: string
  ): Promise<{ syncedCount: number }> {
    // If logs not provided, fetch from scoresheet_logs
    if (!logs || logs.length === 0) {
      const { data: allLogs, error: allLogsError } = await supabase
        .from('scoresheet_logs')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          player_id,
          match_minute,
          action,
          new_value,
          timestamp,
          user_id,
          position_data,
          stat_types!inner(stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('timestamp', { ascending: true });

      if (allLogsError) {
        throw new Error(`Failed to fetch scoresheet logs: ${allLogsError.message}`);
      }

      // Filter out excluded stat types
      logs = (allLogs || []).filter(log => {
        const statType = log.stat_types as any;
        return !this.EXCLUDED_TIMELINE_STAT_TYPES.includes(statType.stat_key);
      });
    }

    return this.syncMatchTimelineInternal(matchId, logs, userId);
  }

  /**
   * Internal method for syncing match timeline (used by both public method and orchestrator)
   */
  private static async syncMatchTimelineInternal(
    matchId: string,
    logs: any[],
    userId?: string
  ): Promise<{ syncedCount: number }> {
    if (logs.length === 0) {
      return { syncedCount: 0 };
    }

    // Get log IDs we're processing to optimize the query
    const logIds = logs.map(log => log.id).filter(Boolean);
    
    if (logIds.length === 0) {
      return { syncedCount: 0 };
    }

    // Check which timeline events already exist (with their player_id to check if it's missing)
    // Check by source_log_id regardless of is_active status to prevent duplicates
    // Only check for the specific log IDs we're processing for efficiency
    const { data: existingEvents, error: existingError } = await supabase
      .from('match_timeline')
      .select('id, source_log_id, player_id')
      .eq('match_id', matchId)
      .in('source_log_id', logIds);

    if (existingError) {
      throw new Error(`Failed to check existing timeline events: ${existingError.message}`);
    }

    // Create a map: source_log_id -> { event_id, player_id }
    const existingEventsMap = new Map<string, { id: string; player_id: string | null }>();
    existingEvents?.forEach(event => {
      if (event.source_log_id) {
        existingEventsMap.set(event.source_log_id, {
          id: event.id,
          player_id: event.player_id
        });
      }
    });

    // Separate logs into new events and events that need updates
    const newEvents: any[] = [];
    const eventsToUpdate: Array<{ eventId: string; playerId: string }> = [];

    logs.forEach(log => {
      const existingEvent = existingEventsMap.get(log.id);
      
      if (!existingEvent) {
        // New event - add to insert list
        newEvents.push(log);
      } else {
        // Event already exists - check if we need to update player_id
        // Only update if:
        // 1. Log has player_id
        // 2. Existing event's player_id is null (missing)
        if (log.player_id && !existingEvent.player_id) {
          eventsToUpdate.push({
            eventId: existingEvent.id,
            playerId: log.player_id
          });
        }
      }
    });

    let syncedCount = 0;

    // Insert new events
    if (newEvents.length > 0) {
      // Prepare timeline events for insertion
      const timelineEventsToInsert = newEvents.map(log => {
        const statType = log.stat_types as any;
        
        // Extract position data
        const positionExtraction = extractPositionStartEnd(log.position_data);

        // Determine if this is a key event
        const isKeyEvent = this.KEY_EVENT_STAT_TYPES.includes(statType.stat_key);

        // Generate tags
        const tags = this.generateEventTags(statType.stat_key, log);

        return {
          match_id: log.match_id,
          source_log_id: log.id,
          stat_type_id: log.stat_type_id,
          team_id: log.team_id,
          player_id: log.player_id,
          match_minute: log.match_minute,
          event_timestamp: log.timestamp,
          position_start: positionExtraction.position_start,
          position_end: positionExtraction.position_end,
          tags,
          action: log.action,
          stat_value: log.new_value,
          is_key_event: isKeyEvent,
          is_active: log.action === 'increment' // Decrements default to inactive
        };
      });

      // Insert timeline events
      const { error: insertError } = await supabase
        .from('match_timeline')
        .insert(timelineEventsToInsert);

      if (insertError) {
        throw new Error(`Failed to insert timeline events: ${insertError.message}`);
      }

      syncedCount += timelineEventsToInsert.length;
      console.log(`Inserted ${timelineEventsToInsert.length} new timeline events for match ${matchId}`);
    }

    // Update existing events with missing player_id
    if (eventsToUpdate.length > 0) {
      // Update each event individually to set player_id
      const updatePromises = eventsToUpdate.map(({ eventId, playerId }) =>
        supabase
          .from('match_timeline')
          .update({ player_id: playerId })
          .eq('id', eventId)
      );

      const updateResults = await Promise.all(updatePromises);
      
      // Check for errors
      const errors = updateResults.filter(result => result.error);
      if (errors.length > 0) {
        console.error(`Failed to update ${errors.length} timeline events with player_id`);
        // Don't throw - log error but continue
      } else {
        syncedCount += eventsToUpdate.length;
        console.log(`Updated ${eventsToUpdate.length} existing timeline events with missing player_id for match ${matchId}`);
      }
    }

    if (syncedCount === 0) {
      console.log('No new or updatable timeline events for match:', matchId);
    }

    return { syncedCount };
  }

  /**
   * Sync logs to match_visualization table (only logs with position_data)
   * - Inserts new events that don't exist
   * - Updates existing events ONLY if player_id is missing in destination but exists in source
   * - Never overwrites manually set player_id values
   * 
   * @param matchId - Match ID to sync events for
   * @param logs - Optional array of logs to sync. If not provided, fetches from scoresheet_logs
   * @param userId - Optional user ID for created_by field
   */
  static async syncMatchVisualization(
    matchId: string,
    logs?: any[],
    userId?: string
  ): Promise<{ syncedCount: number }> {
    // If logs not provided, fetch from scoresheet_logs
    if (!logs || logs.length === 0) {
      const { data: allLogs, error: allLogsError } = await supabase
        .from('scoresheet_logs')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          player_id,
          match_minute,
          action,
          new_value,
          timestamp,
          user_id,
          position_data,
          stat_types!inner(stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('timestamp', { ascending: true });

      if (allLogsError) {
        throw new Error(`Failed to fetch scoresheet logs: ${allLogsError.message}`);
      }

      // Filter to logs with position_data
      logs = (allLogs || []).filter(log => hasValidPositionData(log.position_data));
    }

    return this.syncMatchVisualizationInternal(matchId, logs, userId);
  }

  /**
   * Internal method for syncing match visualization (used by both public method and orchestrator)
   */
  private static async syncMatchVisualizationInternal(
    matchId: string,
    logs: any[],
    userId?: string
  ): Promise<{ syncedCount: number }> {
    if (logs.length === 0) {
      return { syncedCount: 0 };
    }

    // Filter out decrement actions - visualization should only have increments
    const incrementLogs = logs.filter(log => log.action === 'increment');

    if (incrementLogs.length === 0) {
      return { syncedCount: 0 };
    }

    // Get log IDs we're processing to optimize the query
    const logIds = incrementLogs.map(log => log.id).filter(Boolean);
    
    if (logIds.length === 0) {
      return { syncedCount: 0 };
    }

    // Check which visualization events already exist (with their player_id to check if it's missing)
    // Check by source_log_id regardless of is_active status to prevent duplicates
    // Only check for the specific log IDs we're processing for efficiency
    // Use chunking for large arrays to avoid Bad Request errors
    let existingEvents: any[] = [];
    const chunkSize = 500; // Smaller chunk size for match_visualization
    for (let i = 0; i < logIds.length; i += chunkSize) {
      const chunk = logIds.slice(i, i + chunkSize);
      if (chunk.length === 0) continue;
      
      const { data, error } = await supabase
        .from('match_visualization')
        .select('id, source_log_id, player_id')
        .eq('match_id', matchId)
        .in('source_log_id', chunk);
      
      if (error) {
        console.error(`Error fetching existing visualization events (chunk ${i / chunkSize + 1}):`, error);
        throw new Error(`Failed to check existing visualization events: ${error.message}`);
      }
      
      if (data) {
        existingEvents.push(...data);
      }
    }

    // Create a map: source_log_id -> { event_id, player_id }
    const existingEventsMap = new Map<string, { id: string; player_id: string | null }>();
    existingEvents?.forEach(event => {
      if (event.source_log_id) {
        existingEventsMap.set(event.source_log_id, {
          id: event.id,
          player_id: event.player_id
        });
      }
    });

    // Separate logs into new events and events that need updates
    const newEvents: any[] = [];
    const eventsToUpdate: Array<{ eventId: string; playerId: string }> = [];

    incrementLogs.forEach(log => {
      const existingEvent = existingEventsMap.get(log.id);
      
      if (!existingEvent) {
        // New event - add to insert list
        newEvents.push(log);
      } else {
        // Event already exists - check if we need to update player_id
        // Only update if:
        // 1. Log has player_id
        // 2. Existing event's player_id is null (missing)
        if (log.player_id && !existingEvent.player_id) {
          eventsToUpdate.push({
            eventId: existingEvent.id,
            playerId: log.player_id
          });
        }
      }
    });

    let syncedCount = 0;

    // Insert new events
    if (newEvents.length > 0) {
      // Prepare visualization events for insertion
      const visualizationEventsToInsert = newEvents.map(log => {
        const statType = log.stat_types as any;

        // Generate tags
        const tags = this.generateEventTags(statType.stat_key, log);

        // Normalize position_data: convert single object {x, y} to array [{x, y}]
        let normalizedPositionData = log.position_data;
        if (normalizedPositionData) {
          // Parse if it's a string
          if (typeof normalizedPositionData === 'string') {
            try {
              normalizedPositionData = JSON.parse(normalizedPositionData);
            } catch (e) {
              console.warn('Failed to parse position_data string:', e);
              normalizedPositionData = null;
            }
          }
          
          // Convert single object to array format
          if (normalizedPositionData && typeof normalizedPositionData === 'object' && !Array.isArray(normalizedPositionData)) {
            // Single object format: {x, y} -> convert to [{x, y}]
            if (typeof normalizedPositionData.x === 'number' && typeof normalizedPositionData.y === 'number') {
              normalizedPositionData = [normalizedPositionData];
            } else {
              normalizedPositionData = null;
            }
          }
        }

        return {
          match_id: log.match_id,
          source_log_id: log.id,
          stat_type_id: log.stat_type_id,
          team_id: log.team_id,
          player_id: log.player_id,
          match_minute: log.match_minute,
          event_timestamp: log.timestamp,
          position_data: normalizedPositionData, // Normalized to array format [{x, y}]
          tags,
          action: log.action,
          stat_value: log.new_value,
          is_active: log.action === 'increment' // Decrements default to inactive
        };
      });

      // Insert visualization events
      const { error: insertError } = await supabase
        .from('match_visualization')
        .insert(visualizationEventsToInsert);

      if (insertError) {
        throw new Error(`Failed to insert visualization events: ${insertError.message}`);
      }

      syncedCount += visualizationEventsToInsert.length;
      console.log(`Inserted ${visualizationEventsToInsert.length} new visualization events for match ${matchId}`);
    }

    // Update existing events with missing player_id
    if (eventsToUpdate.length > 0) {
      // Update each event individually to set player_id
      const updatePromises = eventsToUpdate.map(({ eventId, playerId }) =>
        supabase
          .from('match_visualization')
          .update({ player_id: playerId })
          .eq('id', eventId)
      );

      const updateResults = await Promise.all(updatePromises);
      
      // Check for errors
      const errors = updateResults.filter(result => result.error);
      if (errors.length > 0) {
        console.error(`Failed to update ${errors.length} visualization events with player_id`);
        // Don't throw - log error but continue
      } else {
        syncedCount += eventsToUpdate.length;
        console.log(`Updated ${eventsToUpdate.length} existing visualization events with missing player_id for match ${matchId}`);
      }
    }

    if (syncedCount === 0) {
      console.log('No new or updatable visualization events for match:', matchId);
    }

    return { syncedCount };
  }

  /**
   * Determine if a scoresheet log entry qualifies as a key event
   */
  private static isKeyEvent(statKey: string, action: string, value: number): boolean {
    // Include both increment and decrement actions for key events
    // (decrements will be set to is_active=false by default)

    // Game flow events are always key events
    if (['kick_off', 'end_1st_half', 'start_2nd_half', 'full_time', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half', 'penalty_shootout'].includes(statKey)) {
      return true;
    }

    // Goals are always key events
    if (['shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored'].includes(statKey)) {
      return true;
    }

    // Assists are key events
    if (statKey === 'assist') {
      return true;
    }

    // Own goals are key events
    if (statKey === 'own_goal') {
      return true;
    }

    // Penalty misses are key events
    if (statKey === 'penalty_missed') {
      return true;
    }

    // Penalty shootout stats are key events
    if (['penalty_shootout_scored', 'penalty_shootout_missed'].includes(statKey)) {
      return true;
    }

    // Cards are key events
    if (['yellow_card', 'red_card'].includes(statKey)) {
      return true;
    }

    // Substitutions are key events
    if (statKey === 'substitution') {
      return true;
    }

    // Saves are key events - Updated with new goalkeeper stats
    if (['save_open_play', 'save_penalty', 'penalty_punch', 'penalty_claim'].includes(statKey)) {
      return true;
    }

    return false;
  }

  /**
   * Generate tags for an event based on its type and context
   * Made public so it can be reused by VideoTagsSyncService
   */
  static generateEventTags(statKey: string, log: any): any[] {
    const tags: any[] = [];

    // Add event type tag
    tags.push(statKey);

    // Add category tag
    const statType = log.stat_types as any;
    if (statType.category) {
      tags.push(statType.category);
    }

    // Add result tag
    if (statType.event_result) {
      tags.push(statType.event_result);
    }

    // Add specific tags based on event type
    switch (statKey) {
      case 'shot_inside_box_goal':
        tags.push('inside_box', 'goal');
        break;
      case 'shot_outside_box_goal':
        tags.push('outside_box', 'goal');
        break;
      case 'penalty_scored':
        tags.push('penalty', 'goal');
        break;
      case 'penalty_missed':
        tags.push('penalty', 'missed');
        break;
      case 'yellow_card':
        tags.push('card', 'yellow');
        break;
      case 'red_card':
        tags.push('card', 'red');
        break;
      case 'substitution':
        tags.push('substitution');
        break;
      case 'save_open_play':
        tags.push('save', 'open_play');
        break;
      case 'save_penalty':
        tags.push('save', 'penalty');
        break;
    }

    return tags;
  }

  /**
   * Sync all matches that have scoresheet data but no key events
   */
  static async syncAllMatches(): Promise<void> {
    try {
      // Get all matches that have scoresheet logs but no key events
      const { data: matches, error } = await supabase
        .from('matches')
        .select('id')
        .eq('active', true);

      if (error) {
        throw new Error(`Failed to fetch matches: ${error.message}`);
      }

      if (!matches || matches.length === 0) {
        console.log('No matches found to sync');
        return;
      }

      // Sync each match
      for (const match of matches) {
        try {
          await this.syncMatchEvents(match.id);
        } catch (error) {
          console.error(`Failed to sync match ${match.id}:`, error);
          // Continue with other matches
        }
      }

      console.log(`Completed syncing ${matches.length} matches`);
    } catch (error) {
      console.error('Error syncing all matches:', error);
      throw error;
    }
  }

  /**
   * Get sync status for a match
   */
  static async getSyncStatus(matchId: string): Promise<{
    total_logs: number;
    synced_events: number;
    pending_events: number;
  }> {
    try {
      // Count total scoresheet logs
      const { count: totalLogs, error: logsError } = await supabase
        .from('scoresheet_logs')
        .select('*', { count: 'exact', head: true })
        .eq('match_id', matchId)
        .eq('is_active', true);

      if (logsError) {
        throw new Error(`Failed to count logs: ${logsError.message}`);
      }

      // Count synced events
      const { count: syncedEvents, error: eventsError } = await supabase
        .from('key_match_events')
        .select('*', { count: 'exact', head: true })
        .eq('match_id', matchId)
        .eq('is_active', true);

      if (eventsError) {
        throw new Error(`Failed to count events: ${eventsError.message}`);
      }

      return {
        total_logs: totalLogs || 0,
        synced_events: syncedEvents || 0,
        pending_events: (totalLogs || 0) - (syncedEvents || 0)
      };
    } catch (error) {
      console.error('Error getting sync status:', error);
      throw error;
    }
  }
}
